// src/services/usersApi.js
import axios from 'axios';
import { getJwtToken } from '../utils/auth';
import {getBaseUrl} from "../utils/apiClient";

const providersApiClient = axios.create({
    baseURL: `${getBaseUrl()}/providers`,
});

providersApiClient.interceptors.request.use(async (config) => {
    const token = await getJwtToken(); // Espera a que se obtenga el token
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    //config.headers['bypass-tunnel-reminder'] = 'true';
    return config;
}, (error) => {
    return Promise.reject(error); // Si hay un error, lo propagamos
});

export const registerServiceProvider = async (providerData) => {
    try {
        const token = getJwtToken();
        const response = await providersApiClient.post('/service', providerData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const registerProductsProvider = async (providerData) => {
    try {
        const response = await providersApiClient.post('/products', providerData);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const getProviders = async (
    filters,
    providerType,
    pageSize = Number(process.env.REACT_APP_DEFAULT_PAGE_SIZE) || 12,
    lastDocumentId = null
) => {
    try {
        // Configurar los parámetros de la solicitud
        const requestParams = {
            location: filters.location || 'La Plata',
            keywords: filters.keywords || [],
            categoryName: filters.categoryName || '',
            subcategory: filters.subcategory !== "" ? filters.subcategory : null,
            hasCertificates: filters.hasCertificates === true ? true : null, // Cambio aquí: enviar null en lugar de false
            pageSize,
            lastDocumentId
        };

        // Log de los parámetros enviados en la solicitud

        const response = await providersApiClient.get(`/${providerType}`, {
            params: requestParams
        });

        return response.data; // Devuelve { providers: [...], lastDocumentId: "abc123" }
    } catch (error) {
        throw error;
    }
};

export const usernameAvailable = async (providerId) => {
    try {
        const response = await providersApiClient.get(`/available/${providerId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const getProviderProfile = async (providerId) => {
    try {
        const response = await providersApiClient.get(`/${providerId}/active`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getProviderForEdit = async (providerId) => {
    try {
        const response = await providersApiClient.get(`/${providerId}/for-edit`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getProviderComp = async (providerId) => {
    try {
        const response = await providersApiClient.get(`/${providerId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const updateProviderProfile = async (providerId, type, providerData) => {
    try {
        const response = await providersApiClient.put(`/${type}/${providerId}`, providerData);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const recommendProvider = async (providerId, categoryType, token) => {
    try {
        const response = await providersApiClient.put(`/${categoryType}/${providerId}/recommend`,
            {}, // Cuerpo vacío
            {
                headers: {
                    Authorization: `Bearer ${token}`, // Incluye el token en el encabezado
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const unrecommendProvider = async (providerId, categoryType, token) => {
    try {
        const response = await providersApiClient.put(`/${categoryType}/${providerId}/unrecommend`,
            {}, // Cuerpo vacío
            {
                headers: {
                    Authorization: `Bearer ${token}`, // Incluye el token en el encabezado
                },
            }
        );
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const commentProvider = async (providerId, categoryType, comment) => {
    try {
        const response = await providersApiClient.put(`/${categoryType}/${providerId}/comment`,
            comment
        );
        return response.data;
    } catch (error) {
        throw { status: error.response.status, message: error.response.message };
    }
}

export const answerComment = async (providerId, categoryType, answer, commentId) => {
    try {
        const response = await providersApiClient.put(`/${categoryType}/${providerId}/comment/${commentId}`,
            answer);
        return response.data;
    } catch (error) {
        throw { status: error.response.status, message: error.response.message };
    }
}

export const deleteProvider = async (providerId, providerType) => {
    try {
        const response = await providersApiClient.delete(`/${providerType}/${providerId}`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const getInactiveProviders = async () => {
    try {
        const response = await providersApiClient.get('/notActive');
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const activateProvider = async (providerId) => {
    try {
        const response = await providersApiClient.put(`/${providerId}/activate`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const deactivateProvider = async (providerId) => {
    try {
        const response = await providersApiClient.put(`/${providerId}/deactivate`);
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const listAllProviders = async () => {
    try {
        const response = await providersApiClient.get('/list-all');
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const listServiceProviders = async () => {
    try {
        const response = await providersApiClient.get('/list-service');
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const listProductProviders = async () => {
    try {
        const response = await providersApiClient.get('/list-products');
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const countProviders = async () => {
    try {
        const response = await providersApiClient.get('/count');
        return response.data;
    } catch (error) {
        throw error;
    }
}

export const contactProvider = async (providerId, contactData) => {
    try {
        const response = await providersApiClient.post(`/${providerId}/contact`, contactData);
        return response.data;
    } catch (error) {
        throw error;
    }
}

/**
 * Registra un clic en el botón de WhatsApp de un proveedor
 * @param {string} providerId - ID del proveedor
 * @returns {Promise} - Promise que resuelve cuando se completa la petición
 */
export const trackWhatsAppClick = async (providerId) => {
    try {
        await providersApiClient.post(`/${providerId}/phone-click`);
        return true;
    } catch (error) {
        console.warn("Error al registrar clic en WhatsApp:", error);
        // No lanzamos el error para que sea transparente para el usuario
        return false;
    }
};

/**
 * Registra un clic en un enlace específico de un proveedor (Instagram, sitio web, etc.)
 * @param {string} providerId - ID del proveedor
 * @param {string} linkName - Nombre del enlace (instagram, website, etc.)
 * @returns {Promise} - Promise que resuelve cuando se completa la petición
 */
export const trackLinkClick = async (providerId, linkName) => {
    try {
        await providersApiClient.post(`/${providerId}/link-click`, null, {
            params: { linkName }
        });
        return true;
    } catch (error) {
        console.warn(`Error al registrar clic en enlace ${linkName}:`, error);
        // No lanzamos el error para que sea transparente para el usuario
        return false;
    }
};

/**
 * Incrementa el contador de vistas al perfil de un proveedor
 * @param {string} providerId - ID/username del proveedor
 * @param {string} providerType - Tipo del proveedor ('service' o 'products')
 * @returns {Promise} - Promise que resuelve cuando se completa la petición
 */
export const incrementProfileViews = async (providerId, providerType) => {
    try {
        const endpoint = providerType === 'service' ? 'service' : 'products';
        await providersApiClient.post(`/${endpoint}/${providerId}/profile-views`);
        return true;
    } catch (error) {
        console.warn("Error al registrar vista del perfil:", error);
        // No lanzamos el error para que sea transparente para el usuario
        return false;
    }
};

/**
 * Registra un clic en una foto de trabajo de un proveedor
 * @param {string} providerId - ID del proveedor
 * @returns {Promise} - Promise que resuelve cuando se completa la petición
 */
export const trackWorkPhotoClick = async (providerId) => {
    try {
        await providersApiClient.post(`/${providerId}/work-photo-click`);
        return true;
    } catch (error) {
        console.warn("Error al registrar clic en foto de trabajo:", error);
        // No lanzamos el error para que sea transparente para el usuario
        return false;
    }
};

/**
 * Obtiene las estadísticas de un proveedor
 * Solo el dueño del proveedor o un admin pueden acceder a estas estadísticas
 * @param {string} providerId - ID del proveedor
 * @returns {Promise} - Promise con las estadísticas del proveedor
 */
export const getProviderStats = async (providerId) => {
    try {
        const response = await providersApiClient.get(`/${providerId}/stats`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export default providersApiClient;